/* src/app/StudioUIContext.tsx */
import { createContext, useContext, type ReactNode } from 'react';
import { useAtom } from 'jotai';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { appSettingsAtom } from '../state/settings-atoms';

// WHY: the off-canvas sidebar drawer is deleted in the mobile strip overhaul (REVIEW §3, §5). Mobile
// navigation is now the filmstrip rail; desktop keeps a persistent (non-toggled) sidebar. No component
// owns sidebar-open state any more, so those fields are removed from the context entirely.
interface StudioUIContextValue {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const StudioUIContext = createContext<StudioUIContextValue | undefined>(undefined);

export function StudioUIProvider({ children }: { children: ReactNode }) {
  const [, setSettings] = useAtom(appSettingsAtom);
  const theme = useResolvedTheme();

  const toggleTheme = () => {
    setSettings({
      themeMode: theme === 'light' ? 'dark' : 'light'
    });
  };

  const value: StudioUIContextValue = {
    theme,
    toggleTheme
  };

  return <StudioUIContext.Provider value={value}>{children}</StudioUIContext.Provider>;
}

/** Access the Studio UI context for resolved theme state. Must be used within a StudioUIProvider. */
export function useStudioUI() {
  const context = useContext(StudioUIContext);
  if (context === undefined) {
    throw new Error('useStudioUI must be used within a StudioUIProvider');
  }
  return context;
}
